import { useMutation } from '@tanstack/react-query';
import { TargetedEvent, useEffect, useState } from 'preact/compat';
import { useDispatch } from 'react-redux';
import { trackMP } from 'Shared/mp';

import { patchUserProfileMutationFn } from '@/queries/users';
import { useSelector } from '@/redux/helper';
import { updateUserProperty } from '@/redux/slices/user/actions';
import type { ExperienceOption } from '@/redux/slices/user/types';

import PersonaSelector from '../PersonaSelector';
import { flagAppOnboardingModalStep, setSelectedExperienceOption } from './redux/actions';

const AppOnboardingTailorExperience = () => {
  const dispatch = useDispatch();
  const persona = useSelector((state) => state.user.persona);
  const [selected, setSelected] = useState<ExperienceOption | null>(persona ?? null);

  const { mutate: savePersona, isPending } = useMutation({
    mutationFn: patchUserProfileMutationFn,
    onSuccess: (_data, variables) => {
      const savedPersona = variables.persona;
      if (!savedPersona) return;
      dispatch(updateUserProperty({ persona: savedPersona }));
    },
    onError: (error) => console.error(error),
  });

  const handleSubmit = (e: TargetedEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (selected) savePersona({ persona: selected });
  };

  useEffect(() => {
    if (persona) {
      dispatch(setSelectedExperienceOption(persona));
      dispatch(flagAppOnboardingModalStep('tailorExperience'));
      trackMP('after-signup-step', { step: 'tailorExperience', selectedOption: persona });
    }
  }, [persona, dispatch]);

  return (
    <form onSubmit={handleSubmit} className="flex h-full flex-col">
      <div className="shrink-0">
        <h1 className="preflight preflight-h1 p-5 pt-10 text-center text-2xl/7 font-bold text-[#162B1B]">
          How will you use zoolife?
        </h1>
      </div>
      <div className="min-h-0 grow overflow-y-auto px-10">
        <PersonaSelector className="flex flex-col gap-3" value={selected} onChange={setSelected} />
      </div>
      <div className="flex shrink-0 flex-col justify-center gap-2 px-10 py-5">
        <button
          type="submit"
          disabled={!selected || isPending}
          className="preflight preflight-button w-full rounded-full bg-primary-green p-2 text-sm/6 font-medium text-white hover:bg-secondary-green"
        >
          {isPending ? 'Saving...' : selected === 'educator' ? 'Almost There!' : 'Start'}
        </button>
        <div className="text-center">
          <button
            type="button"
            onClick={() => {
              savePersona({ persona: 'other' });
            }}
            className="preflight preflight-button text-xs text-gray-500 underline underline-offset-2"
          >
            Skip / I don&lsquo;t see myself here
          </button>
        </div>
      </div>
    </form>
  );
};

export default AppOnboardingTailorExperience;
